import type React from 'react';
import { useMemo, useState } from 'react';

import { AgentWizardOptionButton } from '@polyai/ui/components/atoms/AgentWizardOptionButton';
import Button from '@polyai/ui/components/atoms/Button';

import OTFromScreen from 'components/organisms/OTFormScreen/OTFromScreen';
import { usePendo } from 'hooks/usePendo';
import { NEW_INTEGRATION_QUERY_PARAM, REGIONS } from './constants';
import { OPENTABLE_REGION_UI_COPY } from './OpenTableRegion.uiCopy';
import type { Region, RegionOption } from './types';
import { getRedirectURL } from './utils';

interface RegionButtonProps {
  region: RegionOption;
  isSelected: boolean;
  onSelect: () => void;
}

const RegionButton: React.FC<RegionButtonProps> = ({
  region,
  isSelected,
  onSelect,
}) => (
  <AgentWizardOptionButton isSelected={isSelected} onClick={onSelect}>
    <div className="flex items-center gap-xs2">
      <span>{region.icon}</span>
      <span className="text-text-primary font-semibold text-body-regular">
        {region.label}
      </span>
    </div>
  </AgentWizardOptionButton>
);

const OpenTableRegion: React.FC = () => {
  const [selectedRegion, setSelectedRegion] = useState<Region>();

  // Initialize Pendo for anonymous tracking
  usePendo();

  const isNewIntegration = useMemo(
    () =>
      new URLSearchParams(window.location.search).has(
        NEW_INTEGRATION_QUERY_PARAM,
      ),
    [],
  );

  const redirectToRegion = () => {
    if (!selectedRegion) {
      return;
    }

    window.location.href = getRedirectURL(selectedRegion, isNewIntegration);
  };

  return (
    <OTFromScreen>
      <div className="flex flex-col gap-sm4">
        <h1
          className="
            text-text-primary font-semibold text-header-large leading-tight
          "
        >
          {isNewIntegration
            ? OPENTABLE_REGION_UI_COPY.greeting.new_integration
            : OPENTABLE_REGION_UI_COPY.greeting.login}
        </h1>
        <div className="flex gap-xs2 flex-col">
          {REGIONS.map(region => (
            <RegionButton
              key={region.value}
              isSelected={selectedRegion === region.value}
              region={region}
              onSelect={() => setSelectedRegion(region.value)}
            />
          ))}
        </div>
        <Button
          disabled={!selectedRegion}
          label="Continue"
          fullWidth
          onClick={redirectToRegion}
        />
        <p className="text-text-primary text-body-regular font-semibold leading-[1.2]">
          {OPENTABLE_REGION_UI_COPY.enterprise.prefix}{' '}
          <a
            href="https://poly.ai/gordon#host-form"
            target="_blank"
            rel="noopener noreferrer"
            className="underline inline"
          >
            {OPENTABLE_REGION_UI_COPY.enterprise.link}
          </a>
        </p>
      </div>
    </OTFromScreen>
  );
};

export default OpenTableRegion;
